import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { PerformanceTile } from '../../../utils/monthlyRevenueUtils';

interface PerformanceTilesProps {
  tiles: PerformanceTile[];
}

const accentMap: Record<string, { bar: string; bg: string; text: string; icon: string }> = {
  emerald: { bar: 'bg-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'text-emerald-500' },
  blue: { bar: 'bg-blue-500', bg: 'bg-blue-50', text: 'text-blue-700', icon: 'text-blue-500' },
  amber: { bar: 'bg-amber-500', bg: 'bg-amber-50', text: 'text-amber-700', icon: 'text-amber-500' },
  red: { bar: 'bg-red-500', bg: 'bg-red-50', text: 'text-red-700', icon: 'text-red-500' },
  violet: { bar: 'bg-violet-500', bg: 'bg-violet-50', text: 'text-violet-700', icon: 'text-violet-500' },
};

const TrendIcon: React.FC<{ trend: 'up' | 'down' | 'neutral'; accentKey: string }> = ({ trend, accentKey }) => {
  const cls = accentMap[accentKey]?.icon ?? 'text-slate-500';
  if (trend === 'up') return <TrendingUp size={16} className={cls} />;
  if (trend === 'down') return <TrendingDown size={16} className={cls} />;
  return <Minus size={16} className="text-slate-400" />;
};

export const PerformanceTiles: React.FC<PerformanceTilesProps> = ({ tiles }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
    <div className="flex items-center gap-2 px-4 pt-3 pb-2.5 border-b border-slate-100">
      <div className="w-0.5 h-4 rounded-full bg-amber-500 flex-none" />
      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
        Performance Dashboard
      </span>
    </div>

    <div className="grid grid-cols-2 xl:grid-cols-4 gap-0 divide-x divide-y divide-slate-100">
      {tiles.map(tile => {
        const colors = accentMap[tile.accent] ?? accentMap.blue;
        return (
          <div key={tile.label} className="p-4 flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 leading-tight">
                {tile.label}
              </span>
              <TrendIcon trend={tile.trend} accentKey={tile.accent} />
            </div>
            <div>
              <p className={`text-xl font-bold tabular-nums leading-tight ${colors.text}`}>
                {tile.value}
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5">{tile.sub}</p>
            </div>
            <div className={`h-1 rounded-full ${colors.bar} opacity-60`} />
          </div>
        );
      })}
    </div>
  </div>
);

export default PerformanceTiles;
